import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import {
  FormData,
  PersonalInfoWithId
} from '../../../types/forms';

// Initial form state without legal tab
const initialFormState: FormData = {
  _id: '',
  personal: {
    _id: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dob: '',
    age: 0,
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    county: '',
    postcode: ''
  },
  counselling: {
    _id: '',
    languages: [],
    sessionTypes: [],
    modality: [],
    experience: '',
    availability: [],
    availabilitySlots: [],
    cv: null,
    qualified: undefined,
    qualifiedYears: '',
    supervisedHours: '',
    clientHours: '',
    ongoingSupervision: false,
    supervisorName: '',
    supervisorPhone: '',
    supervisorEmail: '',
    membershipBody: '',
    membershipNumber: '',
    membershipExpiryDate: null,
    membershipType: '',
    insuranceProvider: '',
    insuranceExpiryDate: null,
    insuranceCoverage: '',
    previousExperience: '',
    // qualifiedAndLookingForPlacement field removed as it's deprecated
    experienceOrTraining: [],
    whyInterested: '',
    startDate: ''
  },
  education: {
    _id: '',
    courseName: '',
    learningInstitution: '',
    qualificationType: '',
    hoursCompleted: 0,
    qualifications: []
  },
  referee: {
    _id: '',
    firstReferee: {
      name: '',
      email: '',
      phone: '',
      mobile: '',
      relationship: '',
      yearsKnown: '',
      address: '',
      occupation: '',
      organisation: ''
    },
    secondReferee: {
      name: '',
      email: '',
      phone: '',
      mobile: '',
      relationship: '',
      yearsKnown: '',
      address: '',
      occupation: '',
      organisation: ''
    }
  },
  declaration: false
};

// Calculate age from date of birth
const calculateAge = (dob: string | Date | undefined): number | null => {
  if (!dob) return null;
  
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return null;
  
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const useApplicationForm = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [saveStatus, setSaveStatus] = useState<{ [key: string]: 'idle' | 'saving' | 'saved' | 'error' }>({ 
    personal: 'idle',
    counselling: 'idle',
    education: 'idle',
    referee: 'idle'
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const fetchCounsellorData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch application token validity
      const tokenResponse = await api.get(`/api/counsellors/public/application/${token}/validate`);
      
      if (tokenResponse.status === 200) {
        // Get expiry date from response (could be expiryDate or applicationExpiryDate)
        const expiryDate = tokenResponse.data.expiryDate || tokenResponse.data.applicationExpiryDate;
        setExpirationDate(new Date(expiryDate));
        
        // Check if application has already been submitted
        if (tokenResponse.data.applicationSubmitted) {
          setIsSubmitted(true);
          setError('This application has already been submitted');
          setLoading(false);
          return;
        }
        
        // Check if token is expired
        if (new Date(expiryDate) < new Date()) {
          setError('This application link has expired');
          setLoading(false);
          return;
        }
      } else {
        setError('Invalid application link');
        setLoading(false);
        return;
      }
      
      // Fetch counsellor data
      const counsellorResponse = await api.get(`/api/counsellors/public/application/${token}`);
      
      if (counsellorResponse.status === 200) {
        const counsellorData = counsellorResponse.data;
        console.log('Counsellor data:', counsellorData);
        
        // Check if application is already submitted
        if (counsellorData.applicationStatus === 'submitted') {
          setIsSubmitted(true);
        }
        
        // Get existing application data if available
        const existingApplication = counsellorData.application || {};
        const existingPersonal = existingApplication.personal || {};
        const existingCounselling = existingApplication.counselling || {};
        const existingEducation = existingApplication.education || {};
        const existingReferee = existingApplication.referee || {};
        
        console.log('Existing personal data:', existingPersonal);
        
        // Set form data with existing values or defaults
        const personalData: PersonalInfoWithId = {
          _id: counsellorData._id,
          firstName: counsellorData.firstName || '',
          lastName: counsellorData.lastName || '',
          email: counsellorData.email || '',
          phone: counsellorData.phone || '',
          dob: existingPersonal.dob || counsellorData.dob || '',
          age: calculateAge(existingPersonal.dob || counsellorData.dob) || 0,
          addressLine1: existingPersonal.addressLine1 || '',
          addressLine2: existingPersonal.addressLine2 || '',
          addressLine3: existingPersonal.addressLine3 || '',
          city: existingPersonal.city || '',
          county: existingPersonal.county || '',
          postcode: existingPersonal.postcode || ''
        };

        setFormData({
          _id: counsellorData._id,
          personal: personalData,
          counselling: {
            _id: counsellorData._id,
            languages: existingCounselling.languages || [],
            sessionTypes: existingCounselling.sessionTypes || [],
            modality: existingCounselling.modality || [],
            experience: existingCounselling.experience || '',
            availability: existingCounselling.availability || [],
            availabilitySlots: existingCounselling.availabilitySlots || [],
            cv: existingCounselling.cv || null,
            qualified: existingCounselling.qualified || false,
            qualifiedYears: existingCounselling.qualifiedYears || '',
            supervisedHours: existingCounselling.supervisedHours || '',
            clientHours: existingCounselling.clientHours || '',
            ongoingSupervision: existingCounselling.ongoingSupervision || false,
            supervisorName: existingCounselling.supervisorName || '',
            supervisorPhone: existingCounselling.supervisorPhone || '',
            supervisorEmail: existingCounselling.supervisorEmail || '',
            membershipBody: existingCounselling.membershipBody || '',
            membershipNumber: existingCounselling.membershipNumber || '',
            membershipExpiryDate: existingCounselling.membershipExpiryDate || null,
            membershipType: existingCounselling.membershipType || '',
            insuranceProvider: existingCounselling.insuranceProvider || '',
            insuranceExpiryDate: existingCounselling.insuranceExpiryDate || null,
            insuranceCoverage: existingCounselling.insuranceCoverage || '',
            previousExperience: existingCounselling.previousExperience || '',
            // qualifiedAndLookingForPlacement field removed as it's deprecated
            experienceOrTraining: existingCounselling.experienceOrTraining || [],
            whyInterested: existingCounselling.whyInterested || '',
            startDate: existingCounselling.startDate || ''
          },
          education: {
            _id: counsellorData._id,
            courseName: existingEducation.courseName || '',
            learningInstitution: existingEducation.learningInstitution || '',
            qualificationType: existingEducation.qualificationType || '',
            hoursCompleted: existingEducation.hoursCompleted || 0,
            qualifications: existingEducation.qualifications || []
          },
          referee: {
            _id: counsellorData._id,
            firstReferee: {
              name: existingReferee.firstReferee?.name || '',
              email: existingReferee.firstReferee?.email || '',
              phone: existingReferee.firstReferee?.phone || '',
              mobile: existingReferee.firstReferee?.mobile || '',
              relationship: existingReferee.firstReferee?.relationship || '',
              yearsKnown: existingReferee.firstReferee?.yearsKnown || '',
              address: existingReferee.firstReferee?.address || '',
              occupation: existingReferee.firstReferee?.occupation || '',
              organisation: existingReferee.firstReferee?.organisation || ''
            },
            secondReferee: {
              name: existingReferee.secondReferee?.name || '',
              email: existingReferee.secondReferee?.email || '',
              phone: existingReferee.secondReferee?.phone || '',
              mobile: existingReferee.secondReferee?.mobile || '',
              relationship: existingReferee.secondReferee?.relationship || '',
              yearsKnown: existingReferee.secondReferee?.yearsKnown || '',
              address: existingReferee.secondReferee?.address || '',
              occupation: existingReferee.secondReferee?.occupation || '',
              organisation: existingReferee.secondReferee?.organisation || ''
            }
          },
          declaration: existingApplication.declaration || false
        });
      }
    } catch (err: any) {
      console.error('Error fetching counsellor data:', err);
      setError(err.response?.data?.error || 'Error fetching counsellor data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchCounsellorData();
    } else {
      setLoading(false);
      setError('No token provided');
    }
  }, [token, fetchCounsellorData]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTabChange = (_event: React.BaseSyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    scrollToTop();
  };

  const handleTabNavigation = (newTab: number) => {
    setActiveTab(newTab);
    scrollToTop();
  };

  const handleSubmitForm = async () => {
    try {
      setSubmitting(true);
      setValidationErrors([]);

      // Validate all sections
      const personalValidation = validateSectionForSubmit('personal');
      const counsellingValidation = validateSectionForSubmit('counselling');
      const educationValidation = validateSectionForSubmit('education');
      const refereeValidation = validateSectionForSubmit('referee');

      const allErrors = [
        ...personalValidation.errors,
        ...counsellingValidation.errors,
        ...educationValidation.errors,
        ...refereeValidation.errors
      ];

      if (allErrors.length > 0) {
        setValidationErrors(allErrors);
        setValidationDialogOpen(true);
        setSubmitting(false);
        return;
      }

      // Submit form
      const url = `/api/counsellors/public/application/${token}/submit`;
      console.log('Submitting form to URL:', url);
      
      const response = await api.post(url, formData);

      if (response.status === 200) {
        setIsSubmitted(true);
        navigate('/application-success');
      }
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError(err.response?.data?.error || 'Error submitting form');
    } finally {
      setSubmitting(false);
    }
  };

  const validateSectionForSubmit = (section: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    let isValid = true;

    if (section === 'personal') {
      if (!formData.personal.firstName) errors.push('First Name is required');
      if (!formData.personal.lastName) errors.push('Last Name is required');
      if (!formData.personal.email) errors.push('Email is required');
      if (!formData.personal.phone) errors.push('Phone is required');
      if (!formData.personal.dob) errors.push('Date of Birth is required');
      if (!formData.personal.addressLine1 || !formData.personal.addressLine1.trim()) errors.push('Address Line 1 is required');
      if (!formData.personal.city || !formData.personal.city.trim()) errors.push('City is required');
      if (!formData.personal.postcode || !formData.personal.postcode.trim()) errors.push('Postcode is required');
      if (!formData.counselling.cv) errors.push('CV is required');
    } else if (section === 'education') {
      if (!formData.education.courseName) errors.push('Course Name is required');
      if (!formData.education.learningInstitution) errors.push('Learning Institution is required');
      if (!formData.education.qualificationType) errors.push('Qualification Type is required');
      if (!formData.education.hoursCompleted) errors.push('Hours Completed is required');
    } else if (section === 'referee') {
      if (!formData.referee.firstReferee.name) errors.push('Professional Referee Name is required');
      if (!formData.referee.firstReferee.email) errors.push('Professional Referee Email is required');
      if (!formData.referee.firstReferee.mobile) errors.push('Professional Referee Mobile is required');
      if (!formData.referee.firstReferee.relationship) errors.push('Professional Referee Relationship is required');
      if (!formData.referee.secondReferee.name) errors.push('Personal Referee Name is required');
      if (!formData.referee.secondReferee.email) errors.push('Personal Referee Email is required');
      if (!formData.referee.secondReferee.mobile) errors.push('Personal Referee Mobile is required');
      if (!formData.referee.secondReferee.relationship) errors.push('Personal Referee Relationship is required');
    }

    if (errors.length > 0) {
      isValid = false;
    }

    return { isValid, errors };
  };

  const navigateToSectionWithErrors = () => {
    setValidationDialogOpen(false);
    
    // Check each section and navigate to the first one with errors
    const personalValidation = validateSectionForSubmit('personal');
    if (!personalValidation.isValid) {
      handleTabNavigation(0);
      return;
    }
    
    const counsellingValidation = validateSectionForSubmit('counselling');
    if (!counsellingValidation.isValid) {
      handleTabNavigation(1);
      return;
    }
    
    const educationValidation = validateSectionForSubmit('education');
    if (!educationValidation.isValid) {
      handleTabNavigation(2);
      return;
    }
    
    const refereeValidation = validateSectionForSubmit('referee');
    if (!refereeValidation.isValid) {
      handleTabNavigation(3);
      return;
    }
  };

  return {
    activeTab,
    setActiveTab,
    formData,
    setFormData,
    saveStatus,
    setSaveStatus,
    isSubmitted,
    expirationDate,
    error,
    loading,
    submitting,
    validationErrors,
    validationDialogOpen,
    setValidationDialogOpen,
    token,
    handleTabChange,
    handleTabNavigation,
    handleSubmitForm,
    validateSectionForSubmit,
    navigateToSectionWithErrors
  };
};

export default useApplicationForm;
